// 用户登录页面 - 现代简约风格
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { LogIn, Mail, Lock, Eye, EyeOff, RefreshCw, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Toast from '../components/Toast';
import LanguageSelector from '../components/LanguageSelector';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<'client' | 'server' | ''>('');
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [showVerificationSuccessModal, setShowVerificationSuccessModal] = useState(false);
  const navigate = useNavigate();

  const [successMessage, setSuccessMessage] = useState('');

  const [toastConfig, setToastConfig] = useState<{
  isVisible: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
}>({
  isVisible: false,
  type: 'success',
  title: '',
  message: ''
});



  // 处理GitHub登录回调
  useEffect(() => {
    console.log('useEffect执行了'); // 添加这行
  console.log('当前URL:', window.location.href); // 添加这行
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const apiKey = urlParams.get('api_key');
    const email = urlParams.get('email');
    const errorParam = urlParams.get('error');

    if (success === 'true' && apiKey) {
      // GitHub登录成功
      localStorage.setItem('apiKey', apiKey);
      if (email) {
        localStorage.setItem('email', decodeURIComponent(email));
        localStorage.setItem('username', decodeURIComponent(email).split('@')[0]);
      }
      
      // 清除URL参数并跳转
      window.history.replaceState({}, document.title, '/login');
      // 检查是否有重定向参数
const urlParams = new URLSearchParams(window.location.search);
const redirectPath = urlParams.get('redirect');
navigate(redirectPath || '/dashboard/models');
    } else if (errorParam) {
      // GitHub登录失败，显示错误信息
      const errorMessages: { [key: string]: string } = {
        'access_denied': t('githubLoginCancelled'),
        'token_error': t('githubTokenError'),
        'no_email': t('githubNoEmail'),
        'create_user_failed': t('createUserFailed'),
        'create_key_failed': t('createKeyFailed'),
        'oauth_failed': t('githubLoginFailed')
      };
      
      setError(errorMessages[errorParam] || t('githubLoginFailed'));
      setErrorType('server');
      
      // 清除URL参数
      window.history.replaceState({}, document.title, '/login');
    }
    // 检测邮箱验证成功 - 处理查询参数和fragment参数
const verified = urlParams.get('verified');
let accessToken = urlParams.get('access_token');
let tokenType = urlParams.get('type');

// 如果查询参数中没有，检查fragment参数
if (!accessToken && window.location.hash) {
  const fragmentParams = new URLSearchParams(window.location.hash.substring(1));
  accessToken = fragmentParams.get('access_token');
  tokenType = fragmentParams.get('type');
}

// 添加调试信息
console.log('URL参数调试:', {
  verified,
  accessToken: accessToken ? 'EXISTS' : 'NOT_FOUND',
  tokenType,
  hash: window.location.hash,
  search: window.location.search
});

if (verified === 'true' || (accessToken && tokenType === 'signup')) {
  console.log('应该显示验证成功弹窗');
  setShowVerificationSuccessModal(true);
  // 清理URL参数和fragment
  window.history.replaceState({}, document.title, '/login');
}
  }, [navigate]);

  const handleVerificationSuccessClose = () => {
    setShowVerificationSuccessModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError('');
  
  // 自定义验证
  if (!email.trim()) {
    setError(t('pleaseEnterEmail'));
    setErrorType('client');
    return;
  }
  if (!email.includes('@') || !email.includes('.')) {
    setError(t('pleaseEnterValidEmail'));
    setErrorType('client');
    return;
  }
  if (!password.trim()) {
    setError(t('pleaseEnterPassword'));
    setErrorType('client');
    return;
  }
  
  setLoading(true);
  
  try {
    const response = await api.post('/login', {
      email,
      password,
    });

    const { api_key, user_id } = response.data;
localStorage.setItem('apiKey', api_key);

// 立即获取完整用户信息并保存
try {
  const profileResponse = await api.get('/api/user/profile', {
    headers: {
      'x-api-key': api_key
    }
  });
  
  if (profileResponse.data) {
    localStorage.setItem('email', profileResponse.data.email || email);
    localStorage.setItem('username', profileResponse.data.username || 'User');
  }
} catch (err) {
  console.error('Failed to fetch user profile after login:', err);
  // 保存基本信息作为后备
  localStorage.setItem('email', email);
  localStorage.setItem('username', email.split('@')[0]);
}

// 检查是否有重定向参数
const urlParams = new URLSearchParams(window.location.search);
const redirectPath = urlParams.get('redirect');
navigate(redirectPath || '/dashboard/models');
  } catch (err: any) {
    let errorMessage = t('somethingWentWrong');

    if (err.response) {
      const status = err.response.status;
      const serverMessage = err.response?.data?.error?.message || err.response?.data?.message;
      const errorType = err.response?.data?.error?.type;

      console.log('Login error details:', { status, serverMessage, errorType, fullResponse: err.response.data });

      switch (status) {
        case 400:
          if (errorType === 'validation_error') {
            errorMessage = serverMessage || t('checkEmailPasswordFormat');
          } else {
            errorMessage = serverMessage || t('invalidLoginInfo');
          }
          break;
        case 401:
          if (errorType === 'authentication_error') {
            if (serverMessage?.toLowerCase().includes('invalid email or password')) {
              errorMessage = t('incorrectEmailPassword');
            } else if (serverMessage?.toLowerCase().includes('email')) {
              errorMessage = t('emailNotRegistered');
            } else if (serverMessage?.toLowerCase().includes('password')) {
              errorMessage = t('incorrectPassword');
            } else {
              errorMessage = t('loginFailed');
            }
          } else {
            errorMessage = serverMessage || t('authenticationFailed');
          }
          break;
        case 403:
          errorMessage = t('accountRestricted');
          break;
        case 404:
          errorMessage = t('accountNotFound');
          break;
        case 422:
          if (errorType === 'email_verification_required' || serverMessage?.toLowerCase().includes('email not verified') || serverMessage?.toLowerCase().includes('not confirmed')) {
            setUnverifiedEmail(email);
            setShowEmailVerificationModal(true);
            return; // 不设置错误信息，直接显示弹窗
          } else {
            errorMessage = serverMessage || t('verifyEmailFirst');
          }
          break;
        case 429:
          errorMessage = t('tooManyAttempts');
          break;
        case 500:
          if (errorType === 'internal_error') {
            errorMessage = t('serverError');
          } else {
            errorMessage = t('serverErrorLater');
          }
          break;
        case 503:
          errorMessage = t('serviceUnavailable');
          break;
        default:
          errorMessage = serverMessage || `Login failed with error ${status}. Please try again or contact support.`;
      }
    } else if (err.request) {
      errorMessage = t('networkFailed');
    } else {
      errorMessage = t('loginRequestFailed');
    }

    setError(errorMessage);
    setErrorType('server');
    
    // 添加调试信息
    console.error('Login error:', err);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen flex">
      {/* 左侧操作区域 */}
      <div className="w-1/2 bg-white flex items-center justify-center px-8 lg:px-16">
        <div className="w-full max-w-md">
          {/* Logo和标题 */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <img src={`${process.env.PUBLIC_URL}/mplogo.png`} alt="ModelPool" className="h-10 w-10 mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">ModelPool</h1>
              </div>
              <LanguageSelector />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('welcomeBack')}</h2>
            <p className="text-gray-600">{t('signInToAccess')}</p>
          </div>

          {/* 登录表单 */}
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center space-x-2">
                <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* 邮箱输入 */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{t('emailAddress')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error && errorType === 'client' && e.target.value) {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500"
                placeholder={t('enterEmail')}
                title="Please enter a valid email address"
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
            </div>

            {/* 密码输入 */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{t('password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error && errorType === 'client') {
                      setError('');
                      setErrorType('');
                    }
                  }}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500"
                  placeholder={t('enterPassword')}
                  title="Please enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition duration-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="spinner"></div>
                  <span>{t('signingIn')}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <LogIn className="h-5 w-5" />
                  <span>{t('signIn')}</span>
                </div>
              )}
            </button>

            {/* 忘记密码和注册链接 */}
            <div className="space-y-4 text-center">
              <Link 
                to="/forgot-password" 
                className="block text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
              >
                {t('forgotPassword')}
              </Link>
              <div className="text-sm text-gray-600">
                {t('dontHaveAccount')}{' '}
                <Link 
                  to="/register" 
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  {t('createAccount')}
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 右侧营销区域 */}
      <div className="w-1/2 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 flex items-center justify-center px-8 lg:px-16">
        <div className="text-center text-white max-w-lg">
          <h2 className="text-4xl font-bold mb-6 leading-tight">
            {t('unlockPower')}
            <span className="block bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
              {t('aiInnovation')}
            </span>
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold mb-3">{t('access100Models')}</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {t('access100ModelsDesc')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold mb-3">{t('premiumFeatures')}</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {t('premiumFeaturesDesc')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold mb-3">{t('earnWhileShare')}</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {t('earnWhileShareDesc')}
              </p>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10">
            <p className="text-white/80 text-xs italic">
              {t('testimonial')}
            </p>
            <p className="text-white/60 text-xs mt-2">{t('leadingDeveloper')}</p>
          </div>
        </div>
      </div>
      
      {/* 邮箱验证提示弹窗 */}
      {showEmailVerificationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('emailVerificationRequired')}</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                <span className="font-semibold text-amber-600">{unverifiedEmail}</span> {t('emailNotVerified')}
              </p>
              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center space-x-2 mb-6">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{successMessage}</span>
                </div>
              )}
              <div className="space-y-4">
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await api.post('/resend-verification', { email: unverifiedEmail });
                      
                      setShowEmailVerificationModal(false);
                      setUnverifiedEmail('');
                      setSuccessMessage('');
                      setToastConfig({
                        isVisible: true,
                        type: 'success',
                        title: 'Verification Email Sent!',
                        message: 'Please check your inbox and click the verification link.'
                      });
                      
                    } catch (err: any) {
                      console.error('Failed to resend verification email:', err);
                      const errorMessage = err.response?.data?.error?.message || 'Failed to resend verification email. Please try again.';
                      
                      setToastConfig({
                        isVisible: true,
                        type: 'error',
                        title: 'Send Failed',
                        message: errorMessage
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="w-full bg-amber-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-amber-700 focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  <span>Resend Verification Email</span>
                </button>
                <button
                  onClick={() => {
                    setShowEmailVerificationModal(false);
                    setUnverifiedEmail('');
                    setSuccessMessage('');
                  }}
                  className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 验证成功提示弹窗 */}
      {showVerificationSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {t('emailVerifiedSuccess')}
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t('emailVerifiedDesc')}
              </p>
              <button
                onClick={handleVerificationSuccessClose}
                className="w-full bg-green-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200"
              >
                {t('continueToLogin')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Toast 通知组件 */}
      <Toast
        type={toastConfig.type}
        title={toastConfig.title}
        message={toastConfig.message}
        isVisible={toastConfig.isVisible}
        onClose={() => setToastConfig(prev => ({ ...prev, isVisible: false }))}
        duration={4000}
      />
    </div>
  );
};

export default Login;